import { signal, type Signal } from '@preact/signals';

const PIN_KEY = 'jalsa.duty.pin';
const DEFAULT_TIMEOUT_MS = 5 * 60_000;

/**
 * Bildschirmsperre nach Untätigkeit.
 *
 * Sie schützt gegen das kurz abgelegte, entsperrte Gerät. Nicht gegen
 * jemanden, der das Gerät in der Hand hat. Deshalb liegt die PIN im
 * Klartext im lokalen Speicher: vier Ziffern zu hashen wäre Theater
 * (zehntausend Möglichkeiten sind in Millisekunden durchprobiert), und der
 * eigentliche Schutz der Daten ist der verschlüsselte Snapshot aus P9 samt
 * Fernwiderruf des Geräts.
 *
 * Genau so steht es auch in docs/04. Die Sperre ist bewusst kein
 * Sicherheitsanker, sondern eine Bequemlichkeitshürde.
 */
export class ScreenLock {
  readonly locked: Signal<boolean> = signal(false);
  private lastActivity: number;

  constructor(
    private readonly timeoutMs = DEFAULT_TIMEOUT_MS,
    private readonly now: () => number = () => Date.now(),
    private readonly storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = localStorage,
  ) {
    this.lastActivity = this.now();
  }

  get hasPin(): boolean {
    return Boolean(this.storage.getItem(PIN_KEY));
  }

  setPin(pin: string): void {
    if (!/^\d{4}$/.test(pin)) throw new Error('Die PIN besteht aus vier Ziffern.');
    this.storage.setItem(PIN_KEY, pin);
  }

  clearPin(): void {
    this.storage.removeItem(PIN_KEY);
  }

  /** Jede Bedienung schiebt die Sperre nach hinten. */
  touch(): void {
    this.lastActivity = this.now();
  }

  /** Regelmäßig aufzurufen; sperrt, sobald die Frist überschritten ist. */
  tick(): void {
    if (!this.hasPin || this.locked.value) return;
    if (this.now() - this.lastActivity >= this.timeoutMs) this.locked.value = true;
  }

  lock(): void {
    if (this.hasPin) this.locked.value = true;
  }

  unlock(pin: string): boolean {
    if (pin !== this.storage.getItem(PIN_KEY)) return false;
    this.locked.value = false;
    this.touch();
    return true;
  }
}
